import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import {
    Modal,
    Button,
    Space,
    Group,
    TextInput,
    Grid,
    Checkbox,
    Textarea,
    Radio,
    Alert,
    Stack,
    Paper,
    Text,
    Badge,
    ScrollArea
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useForm, UseFormReturnType } from '@mantine/form';
import SenderIDSelect from './SenderIDSelect';
import { UpdateLink, ActionType, FileTag, KYC, KYCPage, RejectionFormValues, UserDataType, BaseResult, CustomClaims, KYCProduct, Customer, ALWAYS_SELECTED_PAGES } from '../types';
import FileTagPicker from "./file_tag_picker";
import { DocTypes } from "./file_card";
import { MyUserDataContext } from "../contexts";
import { httpsCallable } from "firebase/functions";
import { useFunctions, useFirestore } from "../helpers/firebaseContext";
import { notifications } from '@mantine/notifications';
import { collection, getDocs } from "firebase/firestore";
import { KYCPagePreview } from "../routes/kyc/kyc_pages";
import { resolveProductPreviewPages } from "../routes/kyc/kyc_products";

// ==================== CONSTANTS ====================

const FIRESTORE_PATHS = {
    CONFIG: 'config',
    KYC_CONFIG: 'kyc_configuration',
    KYC_PAGES: 'kyc_pages',
    KYC_PRODUCTS: 'kyc_products',
} as const;

const PACI_AUTHENTICATION_PAGE_ID = 'paci_authentication';

/** What the modal knows about the customer's identity, used to decide required pages. */
type CustomerIdentity = {
    isIndividual: boolean;
    hasOtherId: boolean;
    hasCivilID: boolean;
    isDocusign: boolean;
};

const getCustomerIdentity = (kycData: KYC | null, customer?: Customer | null): CustomerIdentity => {
    const completed = kycData?.completedKYCData;
    const prefill = (completed?.prefillData ?? {}) as Record<string, any>;

    const customerType = customer?.customerType
        ?? (customer?.commercial_register || prefill.accountType === 'company' ? 'company' : 'individual');
    const otherId = customer?.other_id ?? prefill.other_id;
    const civilID = customer?.civilID ?? kycData?.civilID ?? completed?.civilID;

    return {
        isIndividual: customerType === 'individual',
        hasOtherId: !!(typeof otherId === 'string' ? otherId.trim() : otherId),
        hasCivilID: !!(typeof civilID === 'string' ? civilID.trim() : civilID),
        isDocusign: completed?.signingMethod === 'docusign',
    };
};

/**
 * PACI authentication is required when the customer can actually authenticate with PACI:
 * - individuals identified by Civil ID (no Other ID on file)
 * - entities (company / trust / other) — the authorized signatory authenticates via PACI
 * It is never required on DocuSign links, where the backend drops the PACI page.
 */
const isPaciRequired = (identity: CustomerIdentity): boolean => {
    if (identity.isDocusign) return false;
    if (!identity.isIndividual) return true;
    return !identity.hasOtherId;
};

const getPaciHint = (identity: CustomerIdentity): string => {
    if (!identity.isIndividual) return 'Required: authorized signatory authenticates via PACI';
    if (identity.hasOtherId) return 'Optional: customer identified by Other ID, PACI not applicable';
    return 'Required: customer identified by Civil ID';
};

// ==================== HOOKS ====================

const useKYCConfigData = (db: any) => {
    const [allKycPages, setAllKycPages] = useState<KYCPage[]>([]);
    const [allKycProducts, setAllKycProducts] = useState<KYCProduct[]>([]);

    useEffect(() => {
        const load = async () => {
            const [pagesSnap, productsSnap] = await Promise.all([
                getDocs(collection(db, FIRESTORE_PATHS.CONFIG, FIRESTORE_PATHS.KYC_CONFIG, FIRESTORE_PATHS.KYC_PAGES)),
                getDocs(collection(db, FIRESTORE_PATHS.CONFIG, FIRESTORE_PATHS.KYC_CONFIG, FIRESTORE_PATHS.KYC_PRODUCTS)),
            ]);
            const pages: KYCPage[] = [];
            pagesSnap.forEach((d) => pages.push(d.data() as KYCPage));
            const products: KYCProduct[] = [];
            productsSnap.forEach((d) => products.push(d.data() as KYCProduct));
            setAllKycPages(pages);
            setAllKycProducts(products);
        };
        load().catch(console.error);
    }, [db]);

    return { allKycPages, allKycProducts };
};

const useProductPages = (allKycPages: KYCPage[], allKycProducts: KYCProduct[]) => {
    const getProductPages = useCallback((productId: string, visited = new Set<string>()): KYCPage[] => {
        if (visited.has(productId)) return [];
        visited.add(productId);

        const product = allKycProducts.find((p) => p.id === productId);
        if (!product?.pages) return [];

        const result: KYCPage[] = [];
        for (const ref of product.pages) {
            if (typeof ref === 'string') {
                const page = allKycPages.find((p) => p.id === ref);
                if (page) result.push(page);
            } else if (ref.type === 'page') {
                const page = allKycPages.find((p) => p.id === ref.id);
                if (page) result.push(page);
            } else if (ref.type === 'product') {
                result.push(...getProductPages(ref.id, visited));
            }
        }
        return result;
    }, [allKycPages, allKycProducts]);

    return { getProductPages };
};

// ==================== PAGE PREVIEW ====================

const SIDE_PREVIEW_SHELL_STYLE: React.CSSProperties = {
    height: 520,
    minHeight: 0,
    maxWidth: "100%",
};

/**
 * Side panel showing what the client will see for every chosen page, in flow order.
 * Mirrors the KYC Configuration product preview, stacked so all pages can be scrolled through.
 */
const KYCSelectedPagesPreview: React.FC<{ pages: KYCPage[] }> = ({ pages }) => (
    <Stack gap="sm" h="100%">
        <div>
            <Text size="sm" fw={500}>Preview</Text>
            <Text size="xs" c="dimmed">
                {pages.length === 0
                    ? "Select pages to see what the client will see."
                    : `${pages.length} ${pages.length === 1 ? "page" : "pages"} the client will update.`}
            </Text>
        </div>

        {pages.length === 0 ? (
            <Paper withBorder radius="md" p="xl" style={{ textAlign: "center" }} bg="gray.0">
                <Text fw={600} size="sm">No pages selected</Text>
                <Text size="xs" c="dimmed" mt={4}>
                    Tick a page on the left to preview it here.
                </Text>
            </Paper>
        ) : (
            <ScrollArea.Autosize mah="calc(100vh - 220px)" offsetScrollbars>
                <Stack gap="lg">
                    {pages.map((page, index) => (
                        <Stack gap={6} key={page.id}>
                            <Group gap={6} wrap="nowrap">
                                <Badge size="sm" variant="filled">{index + 1}</Badge>
                                <Text size="sm" fw={600} lineClamp={1}>
                                    {page.title || page.id}
                                </Text>
                                <Text size="xs" c="dimmed" style={{ fontFamily: "monospace" }}>
                                    {page.id}
                                </Text>
                            </Group>
                            <KYCPagePreview
                                page={page}
                                showIntro={false}
                                language="en"
                                shellStyle={SIDE_PREVIEW_SHELL_STYLE}
                            />
                        </Stack>
                    ))}
                </Stack>
            </ScrollArea.Autosize>
        )}
    </Stack>
);

// ==================== COMPONENT ====================


export interface RejectionModalProps {
    onSubmit: (values: RejectionFormValues) => Promise<void>;
    title?: string;
    submitButtonText?: string;
    submitButtonColor?: string;
    triggerButtonText?: string;
    triggerButtonColor?: string;
    triggerButtonDisabled?: boolean;
    triggerButtonSize?: string;
    enableSendToAgent?: boolean;
    triggerButtonVariant?: string;
    showSMSOptions?: boolean;
    showUpdateLinkOptions?: boolean;
    missingFiles?: FileTag[];
    initialValues?: {
        comments?: string;
        sendSMS?: boolean;
        sendToAgent?: boolean;
        usePACI?: boolean;
        action?: ActionType;
        linkInfo?: Partial<UpdateLink>;
        missingFiles?: FileTag[];
    };
    kycData?: KYC;
    /** Customer the KYC belongs to; used to decide which pages are required (e.g. PACI auth). */
    customer?: Customer | null;
}

const CustomRejectionModal: React.FC<RejectionModalProps> = ({
    onSubmit,
    title = "Reject Service",
    submitButtonText = "Reject",
    submitButtonColor = "red",
    triggerButtonText = "Reject",
    triggerButtonColor = "red",
    triggerButtonDisabled = false,
    enableSendToAgent = false,
    triggerButtonSize = "sm",
    triggerButtonVariant = "filled",
    showSMSOptions = true,
    showUpdateLinkOptions = true,
    initialValues = {},
    kycData = null,
    customer = null,
}) => {
    const [opened, setOpened] = useState(false);
    const [sendSMS, setSendSMS] = useState<boolean>(initialValues.sendSMS ?? false);
    const [sendToAgent, setSendToAgent] = useState<boolean>(enableSendToAgent && (initialValues.sendToAgent ?? false));
    const [usePaci, setUsePaci] = useState<boolean>(initialValues.usePACI ?? true);
    const [confirmDialogError, setConfirmDialogError] = useState<string | null>(null);
    const [confirming, setConfirming] = useState<boolean>(false);
    const [hasCompletedKYCData, setHasCompletedKYCData] = useState<boolean>(false);
    const [availablePages, setAvailablePages] = useState<KYCPage[]>([]);
    const [selectedPages, setSelectedPages] = useState<string[]>([]);
    const [openedNewKYC, setOpenedNewKYC] = useState(false);
    const userData: UserDataType = useContext(MyUserDataContext);
    let userClaims = userData.firebaseTokenResult?.claims.customClaims as CustomClaims || {};
    const functions = useFunctions();
    functions.region = "europe-west1";
    const contractKYCUpdate = httpsCallable<RejectionFormValues, BaseResult>(functions, "contractKYCUpdate");


    const firestore = useFirestore();
    const { allKycPages, allKycProducts } = useKYCConfigData(firestore);
    const { getProductPages } = useProductPages(allKycPages, allKycProducts);

    const customerIdentity = useMemo(() => getCustomerIdentity(kycData, customer), [kycData, customer]);
    const paciRequired = isPaciRequired(customerIdentity);

    // Pages that are locked (always selected) for this customer.
    const requiredPageIds = useMemo(() => {
        const ids = new Set<string>(ALWAYS_SELECTED_PAGES);
        if (paciRequired) ids.add(PACI_AUTHENTICATION_PAGE_ID);
        return ids;
    }, [paciRequired]);

    const isRequiredPage = (pageId: string) => requiredPageIds.has(pageId);

    const getRequiredPages = (pages: KYCPage[]) =>
        pages.filter(p => requiredPageIds.has(p.id)).map(p => p.id);

    useEffect(() => {
        if (!kycData?.completedKYCData) return;
        if (allKycPages.length === 0 || allKycProducts.length === 0) return;

        setHasCompletedKYCData(true);

        const productId = kycData.completedKYCData.productId;
        if (!productId) return;

        let resolvedPages = getProductPages(productId);

        // The PACI page must always be offered (it's dropped by the backend on DocuSign links).
        // If the product doesn't reference it, pull it from the page catalogue and place it
        // right after the requirements page, where the app shows it.
        const paciPage = allKycPages.find(p => p.id === PACI_AUTHENTICATION_PAGE_ID);
        const hasPaci = resolvedPages.some(p => p.id === PACI_AUTHENTICATION_PAGE_ID);
        if (customerIdentity.isDocusign) {
            resolvedPages = resolvedPages.filter(p => p.id !== PACI_AUTHENTICATION_PAGE_ID);
        } else if (!hasPaci && paciPage) {
            const requirementsIndex = resolvedPages.findIndex(p => p.id === 'kyc_requirements');
            resolvedPages = [...resolvedPages];
            resolvedPages.splice(requirementsIndex + 1, 0, paciPage);
        }

        setAvailablePages(resolvedPages);

        // Always pre-select locked pages
        setSelectedPages(resolvedPages.filter(p => requiredPageIds.has(p.id)).map(p => p.id));

    }, [kycData, allKycPages, allKycProducts, getProductPages, requiredPageIds, customerIdentity.isDocusign]);

    const kycPages = availablePages.map(p => p.id);

    // Resolve pages the same way the KYC Configuration preview does (overrides,
    // hidden sections, page variables) so the preview matches what the client sees.
    const previewPagesById = useMemo(() => {
        const map = new Map<string, KYCPage>();
        const productId = kycData?.completedKYCData?.productId;
        const product = productId ? allKycProducts.find(p => p.id === productId) : undefined;
        if (!product) return map;
        try {
            const { pages } = resolveProductPreviewPages(product, allKycProducts, allKycPages);
            pages.forEach(({ page }) => {
                if (!map.has(page.id)) map.set(page.id, page);
            });
        } catch (error) {
            console.error("Failed to resolve KYC preview pages:", error);
        }
        return map;
    }, [kycData?.completedKYCData?.productId, allKycProducts, allKycPages]);

    // Chosen pages in KYC flow order, resolved for preview
    const selectedPreviewPages = useMemo(() => {
        const selectedSet = new Set(selectedPages);
        return availablePages
            .filter(p => selectedSet.has(p.id))
            .map(p => previewPagesById.get(p.id) ?? p);
    }, [availablePages, selectedPages, previewPagesById]);

    useEffect(() => {

        setSendSMS(initialValues.sendSMS ?? false);
        setUsePaci(initialValues.usePACI ?? true);
    }, [initialValues.sendSMS, initialValues.usePACI]);

    const form: UseFormReturnType<RejectionFormValues> = useForm<RejectionFormValues>({
        initialValues: {
            comments: initialValues?.comments || '',
            sendSMS: initialValues?.sendSMS || false,
            usePACI: initialValues?.usePACI || true,
            action: initialValues?.action as ActionType,
            missingFiles: initialValues?.missingFiles || [],
            linkInfo: {
                updateKycID: kycData?.id,
                smsMessage: initialValues?.linkInfo?.smsMessage || "",
                updateType: initialValues?.linkInfo?.updateType || "none",
                senderID: initialValues?.linkInfo?.senderID || "",
                usePACI: initialValues?.linkInfo?.usePACI,
                ...initialValues?.linkInfo
            },
        }
    });

    useEffect(() => {
        if (initialValues.sendToAgent) {

            setSendToAgent(initialValues.sendToAgent ?? false);
            setSendSMS(false);
            setUsePaci(false);
        }
    }, [initialValues.sendToAgent]);

    useEffect(() => {
        form.setFieldValue('linkInfo.updateKycID', kycData?.id);
    }, [kycData?.id]);

    useEffect(() => {
        if (sendToAgent) {
            form.setFieldValue('action', "needs_agent_update");
            form.setFieldValue('linkInfo.updateType', "needs_agent_update");

        }
        else {
            form.setFieldValue('action', initialValues?.action as ActionType);
            form.setFieldValue('linkInfo.updateType', initialValues?.linkInfo?.updateType || "none");

        }
    }, [sendToAgent])

    const handleClose = () => {
        setConfirmDialogError(null);
        setSendSMS(initialValues.sendSMS ?? false);
        setUsePaci(initialValues.usePACI ?? true);
        setSelectedPages([]);
        form.reset();
        setOpened(false);
        setOpenedNewKYC(false);
    };

    const handleOpen = () => {
        if (hasCompletedKYCData) {
            // Always reinitialize required pages on every open
            setSelectedPages(getRequiredPages(availablePages));
            setOpenedNewKYC(true);
        } else {
            setOpened(true);
        }
    };

    const handlePageToggle = (pageId: string) => {
        if (isRequiredPage(pageId)) return; // can't uncheck required pages
        setSelectedPages(prev =>
            prev.includes(pageId)
                ? prev.filter(id => id !== pageId)
                : [...prev, pageId]
        );
    };

    const handleSubmit = async (values: RejectionFormValues) => {
        setConfirming(true);
        setConfirmDialogError(null);

        try {
            const formData = {
                ...values,
                sendSMS,
                usePACI: usePaci,
                missingFiles: values.missingFiles || []
            };
            if (hasCompletedKYCData && values.linkInfo?.updateType !== "none") {

                const token = await userData.getAzureADToken!(userData);
                if (!token && !userClaims?.kfh_rm_approver) {
                    throw new Error("No Microsoft token.  Try refreshing");
                }

                if (!kycData?.customerID) {
                    throw new Error("No Customer ID associated with KYC.");
                }

                const chosenActionValue = values.action === "needs_agent_update" ? "needs_agent_update" : "needs_update";
                // Re-order selected pages to match original KYC page order

                const selectedSet = new Set(selectedPages);
                const selectedPagesReOrder = kycPages.filter(id => selectedSet.has(id));

                let action: ActionType = chosenActionValue;
                formData.selectedPages = selectedPagesReOrder;
                formData.kycID = kycData?.id;
                formData.token = token;
                formData.customerID = kycData?.customerID;
                formData.action = action;
                const res = await contractKYCUpdate(formData);
                console.log("Submitting with selected pages: ", formData);
                console.log("KYC action result:", res);
                notifications.show({
                    title: 'Success',
                    message: 'KYC Update Link Sent',
                    color: 'green',
                });
            } else {
                await onSubmit(formData);
            }
            handleClose();
        } catch (error: any) {
            console.error('Rejection error:', error);
            setConfirmDialogError(error.message || 'An error occurred');
        } finally {
            setConfirming(false);
        }
    };

    const showRightPanel = form.values.linkInfo?.updateType !== "none";

    return (
        <>
            <Button
                color={triggerButtonColor}
                disabled={triggerButtonDisabled}
                onClick={handleOpen}
                size={triggerButtonSize}
                variant={triggerButtonVariant}
            >
                {triggerButtonText}
            </Button>

            {/* Modal */}
            <Modal
                opened={opened}
                onClose={handleClose}
                title={title}
                size="md"
            >
                <form onSubmit={form.onSubmit(handleSubmit)}>
                    <Space h="sm" />

                    {/* Comments Input */}
                    {/* <Group ta="end">
                        <TextInput
                            placeholder="Comments"
                            style={{ flex: 1 }}
                            {...form.getInputProps('comments')}
                        />
                    </Group> */}

                    <Space h="sm" />
                    {enableSendToAgent && <Grid>

                        <Grid.Col span={6}>
                            <Checkbox
                                label="Send to Agent"
                                checked={sendToAgent}
                                disabled={sendSMS}
                                onChange={(event) => setSendToAgent(event.currentTarget.checked)}
                            />
                        </Grid.Col>
                    </Grid>}
                    {/* SMS Options */}
                    {showSMSOptions && !sendToAgent && (
                        <Grid ta="center">
                            <Grid.Col span={6}>
                                <Checkbox
                                    label="Send SMS"
                                    checked={sendSMS}
                                    disabled={sendToAgent}
                                    onChange={(event) => setSendSMS(event.currentTarget.checked)}
                                />
                            </Grid.Col>
                            <Grid.Col span={6}>
                                <Checkbox
                                    disabled={!sendSMS}
                                    label="Use PACI"
                                    checked={usePaci}
                                    onChange={(event) => setUsePaci(event.currentTarget.checked)}
                                />
                            </Grid.Col>

                            {sendSMS && (
                                <Grid.Col span={12}>
                                    <SenderIDSelect
                                        {...form.getInputProps("linkInfo.senderID")}
                                        label="Sender Identity"
                                    />
                                </Grid.Col>
                            )}

                            <Grid.Col span={12}>
                                <Textarea
                                    disabled={!sendSMS}
                                    placeholder="Type in SMS message"
                                    label="Message"
                                    {...form.getInputProps('linkInfo.smsMessage')}
                                    onChange={
                                        (e) => {
                                            form.setFieldValue("linkInfo.smsMessage", e.target.value)
                                            form.setFieldValue("comments", e.target.value)
                                        }
                                    }
                                    minRows={2}
                                    autosize
                                />
                            </Grid.Col>
                        </Grid>
                    )}

                    {/* Update Link Options */}
                    {showUpdateLinkOptions && showSMSOptions && (
                        <>
                            <Space h="sm" />
                            <Radio.Group
                                label="Add update link"
                                description="Allow client to update his KYC?"
                                {...form.getInputProps('linkInfo.updateType')}
                            >
                                <Radio disabled={!sendSMS} value="none" label="None" />
                                <Radio disabled={!sendSMS} value="needs_update" label="Update" />
                                <Radio disabled={!sendSMS} value="upload_docs" label="Upload docs" />
                                {sendToAgent && <Radio disabled={sendSMS} value="needs_agent_update" label="Update (Send to Agent)" />}
                            </Radio.Group>
                        </>
                    )}

                    <Space h="xl" />
                    {form.values.linkInfo?.updateType === "upload_docs" && (
                        <FileTagPicker
                            onFileTagSelect={(files) => {
                                console.log("missing files: ", files);
                                form.setFieldValue('missingFiles', files);
                            }}
                            title="Missing Files"
                            initialValues={form.values.missingFiles || []}
                            customDocTypes={DocTypes}
                        />
                    )}

                    <Space h="xl" />

                    {/* Submit Button */}
                    <Button
                        color={submitButtonColor}
                        type="submit"
                        loading={confirming}
                        fullWidth
                    >
                        {submitButtonText}
                    </Button>

                    {/* Error Display */}
                    {confirmDialogError && (
                        <Alert
                            icon={<IconAlertCircle size={16} />}
                            title="Whoops!"
                            color="red"
                            mt="sm"
                        >
                            {confirmDialogError}
                        </Alert>
                    )}
                </form>
            </Modal>

            {/* New Modal - With KYC Data */}
            <Modal
                opened={openedNewKYC}
                onClose={handleClose}
                title={title}
                size={showRightPanel ? "95rem" : "md"}
            >
                <form onSubmit={form.onSubmit(handleSubmit)}>
                    <Grid>
                        <Grid.Col span={showRightPanel ? 3 : 12}>
                            <Space h="sm" />

                            {/* <Group ta="end">
                                <TextInput
                                    placeholder="Comments"
                                    style={{ flex: 1 }}
                                    {...form.getInputProps('comments')}
                                />
                            </Group> */}

                            <Space h="sm" />

                            {showSMSOptions && (
                                <Grid ta="center">
                                    <Grid.Col span={12}>
                                        <Checkbox
                                            label="Send SMS"
                                            checked={sendSMS}
                                            onChange={(event) => setSendSMS(event.currentTarget.checked)}
                                        />
                                    </Grid.Col>

                                    {sendSMS && (
                                        <Grid.Col span={12}>
                                            <SenderIDSelect
                                                {...form.getInputProps("linkInfo.senderID")}
                                                label="Sender Identity"
                                            />
                                        </Grid.Col>
                                    )}

                                    <Grid.Col span={12}>
                                        <Textarea
                                            disabled={!sendSMS}
                                            placeholder="Type in SMS message"
                                            label="Message"
                                            {...form.getInputProps('linkInfo.smsMessage')}
                                            onChange={
                                                (e) => {
                                                    form.setFieldValue("linkInfo.smsMessage", e.target.value)
                                                    form.setFieldValue("comments", e.target.value)
                                                }
                                            }
                                            minRows={2}
                                            autosize
                                        />
                                    </Grid.Col>
                                </Grid>
                            )}

                            {showUpdateLinkOptions && showSMSOptions && (
                                <>
                                    <Space h="sm" />
                                    <Radio.Group
                                        label="Add update link"
                                        description="Allow client to update his KYC?"
                                        {...form.getInputProps('linkInfo.updateType')}
                                    >
                                        <Radio disabled={!sendSMS} value="none" label="None" />
                                        <Radio disabled={!sendSMS} value="needs_update" label="Update" />
                                        <Radio disabled={!sendToAgent} value="needs_agent_update" label="Update (Send to Agent)" />
                                    </Radio.Group>
                                </>
                            )}

                            <Space h="xl" />

                            <Button
                                color={submitButtonColor}
                                type="submit"
                                loading={confirming}
                                fullWidth
                                disabled={showRightPanel && selectedPages.length === 0}
                            >
                                {submitButtonText}
                                {showRightPanel && selectedPages.length > 0 && ` (${selectedPages.length} selected)`}
                            </Button>

                            {confirmDialogError && (
                                <Alert
                                    icon={<IconAlertCircle size={16} />}
                                    title="Whoops!"
                                    color="red"
                                    mt="sm"
                                >
                                    {confirmDialogError}
                                </Alert>
                            )}
                        </Grid.Col>

                        {showRightPanel && (
                            <Grid.Col span={4}>
                                <Text size="sm" fw={500} mb="sm">
                                    Select Pages to Update
                                </Text>
                                <ScrollArea.Autosize mah="calc(100vh - 220px)" offsetScrollbars>
                                    <Stack gap="sm">
                                        {/* User-selectable pages */}
                                        {availablePages
                                            .filter(p => !isRequiredPage(p.id))
                                            .map((page) => (
                                                <Paper
                                                    key={page.id}
                                                    p="sm"
                                                    withBorder
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handlePageToggle(page.id)}
                                                >
                                                    <Group justify="space-between" wrap="nowrap">
                                                        <div style={{ flex: 1 }}>
                                                            <Text size="sm" fw={500}>{page.title}</Text>
                                                            <Text size="xs" c="dimmed">{page.title_ar} ({page.id})</Text>
                                                            {page.id === PACI_AUTHENTICATION_PAGE_ID && (
                                                                <Text size="xs" c="orange" mt={2}>{getPaciHint(customerIdentity)}</Text>
                                                            )}
                                                        </div>
                                                        <Checkbox
                                                            checked={selectedPages.includes(page.id)}
                                                            onChange={() => handlePageToggle(page.id)}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </Group>
                                                </Paper>
                                            ))}

                                        {/* Always-selected locked pages */}
                                        {availablePages
                                            .filter(p => isRequiredPage(p.id))
                                            .map((page) => (
                                                <Paper
                                                    key={page.id}
                                                    p="sm"
                                                    withBorder
                                                    style={{ cursor: 'not-allowed', opacity: 0.7 }}
                                                    bg="gray.0"
                                                >
                                                    <Group justify="space-between" wrap="nowrap">
                                                        <div style={{ flex: 1 }}>
                                                            <Text size="sm" fw={500}>{page.title}</Text>
                                                            <Text size="xs" c="dimmed">{page.title_ar} ({page.id})</Text>
                                                            <Text size="xs" c="blue" mt={2}>
                                                                {page.id === PACI_AUTHENTICATION_PAGE_ID ? getPaciHint(customerIdentity) : 'Required'}
                                                            </Text>
                                                        </div>
                                                        <Checkbox
                                                            checked={true}
                                                            disabled          // 🔒 visually locked
                                                            onChange={() => { }}
                                                        />
                                                    </Group>
                                                </Paper>
                                            ))
                                        }
                                    </Stack>
                                </ScrollArea.Autosize>
                            </Grid.Col>
                        )}

                        {showRightPanel && (
                            <Grid.Col span={5}>
                                <KYCSelectedPagesPreview pages={selectedPreviewPages} />
                            </Grid.Col>
                        )}
                    </Grid>
                </form>
            </Modal>
        </>
    );
};

export default CustomRejectionModal;
